import { useState } from "react";
import { Column, ColumnDef } from "@tanstack/react-table";
import { DataTableColumnHeader } from "@/components/dataTable/DataTableColumnHeader";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  BadgeCheck,
  CalendarClock,
  Copy,
  DollarSign,
  ExternalLink,
  Eye,
  MoreHorizontal,
  Package as PackageIcon,
  Pencil,
  Trash2,
} from "lucide-react";
import { fuzzyFilter, fuzzySort } from "@/components/dataTable/DataTableUtils";
import { SYSTEM_ORG_UUID } from "@/lib/supabase/supabaseClient";
import { toast } from "sonner";
import { trpcVanilla } from "@/lib/trpc/vanilla";

export type Package = Awaited<
  ReturnType<typeof trpcVanilla.packages.list.query>
>[number];

const HOSTED_BASE_URL =
  import.meta.env.VITE_HOSTED_PLANS_URL || "https://plans.trainrr.app";

const formatCurrency = (amount?: number | null, currency?: string) => {
  if (amount == null) return "-";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency ?? "USD",
      // allow two decimal places since we may be dividing cents -> major units
      maximumFractionDigits: 2,
      currencyDisplay: "narrowSymbol",
    }).format(amount);
  } catch (e) {
    // fallback
    return `${currency ?? ""} ${amount}`;
  }
};

function PackageToggleSwitch({
  checked,
  disabled,
  ariaLabel,
  onToggle,
}: {
  checked: boolean;
  disabled?: boolean;
  ariaLabel: string;
  onToggle?: (newVal: boolean) => Promise<void>;
}) {
  const [isToggling, setIsToggling] = useState(false);
  const [displayed, setDisplayed] = useState(checked);

  if (!isToggling && displayed !== checked) {
    setDisplayed(checked);
  }

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Switch
        checked={displayed}
        disabled={disabled || isToggling}
        aria-label={ariaLabel}
        onCheckedChange={async (newVal) => {
          if (!onToggle || isToggling || disabled) return;
          setDisplayed(newVal);
          setIsToggling(true);
          try {
            await onToggle(newVal);
          } finally {
            setIsToggling(false);
          }
        }}
      />
    </div>
  );
}

export const createPackageColumns = (opts?: {
  canEdit?: boolean;
  onToggleActive?: (pkg: Package, newVal: boolean) => Promise<void>;
  onToggleVisible?: (pkg: Package, newVal: boolean) => Promise<void>;
  orgRef?: string | null;
}) => {
  const {
    onToggleActive,
    onToggleVisible,
    canEdit = true,
    orgRef,
  } = opts ?? {};

  const columns: ColumnDef<Package>[] = [
    {
      id: "name",
      accessorKey: "name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Package"
          icon={<PackageIcon className="h-4 w-4" />}
        />
      ),
      cell: ({ getValue, row, onRowSelected }) => {
        const name = getValue() as string;
        if (!canEdit || !onRowSelected) {
          return <div className="min-w-32 text-left font-medium">{name}</div>;
        }
        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRowSelected?.(row.original as Package);
            }}
            className="min-w-32 text-left font-medium p-2 text-secondary-foreground hover:underline"
          >
            {name}
          </button>
        );
      },
      filterFn: fuzzyFilter,
      sortingFn: fuzzySort,
    },
    {
      id: "price",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Price"
          icon={<DollarSign className="h-4 w-4" />}
        />
      ),
      accessorFn: (row) => ({
        amount: (row as Package).price,
        currency: (row as Package).currency,
      }),
      cell: ({ getValue }) => {
        const v = getValue() as {
          amount?: number | null;
          currency?: string;
        };
        // DB stores price in cents; convert to major units for display
        const major = v.amount != null ? Number(v.amount) / 100 : undefined;
        const priceLabel = formatCurrency(major, v.currency || undefined);
        return <>{priceLabel}</>;
      },
    },
    {
      id: "duration",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Duration"
          icon={<CalendarClock className="h-4 w-4" />}
        />
      ),
      accessorFn: (row) => ({
        plan_type: (row as Package).plan_type,
        duration_count: (row as Package).duration_count,
      }),
      cell: ({ getValue }) => {
        const v = getValue() as {
          plan_type?: string;
          duration_count?: number | null;
        };
        const planType = v.plan_type?.toLowerCase?.();
        if (planType === "one-off") return <div>One-off</div>;
        if (planType === "yearly") {
          if (!v.duration_count) return <div>Until cancelled</div>;
          return <div>{v.duration_count} {v.duration_count === 1 ? "year" : "years"}</div>;
        }
        if (!v.duration_count) return <div>Until cancelled</div>;
        let unit = "";
        if (planType === "weekly") unit = "week";
        else if (planType === "monthly") unit = "month";
        const count = v.duration_count;
        return <div>{`${count} ${unit}${count === 1 ? "" : "s"}`}</div>;
      },
    },
    ...(canEdit
      ? [
          {
            id: "is_active",
            accessorKey: "is_active",
            header: ({ column }: { column: Column<Package, unknown> }) => (
              <DataTableColumnHeader
                column={column}
                title="Active"
                icon={<BadgeCheck className="h-4 w-4" />}
              />
            ),
            cell: ({ row }: { row: { original: Package } }) => {
              const pkg = row.original;
              const active = !!pkg.is_active;
              return (
                <PackageToggleSwitch
                  checked={active}
                  ariaLabel={active ? "Active" : "Inactive"}
                  onToggle={
                    onToggleActive
                      ? (newVal) => onToggleActive(pkg, newVal)
                      : undefined
                  }
                />
              );
            },
          },
          {
            id: "is_visible",
            accessorKey: "is_visible",
            header: ({ column }: { column: Column<Package, unknown> }) => (
              <DataTableColumnHeader
                column={column}
                title="Visible"
                icon={<Eye className="h-4 w-4" />}
              />
            ),
            cell: ({ row }: { row: { original: Package } }) => {
              const pkg = row.original;
              const active = !!pkg.is_active;
              const visible = !!pkg.is_visible;
              return (
                <PackageToggleSwitch
                  checked={active ? visible : false}
                  disabled={!active}
                  ariaLabel={visible ? "Visible" : "Hidden"}
                  onToggle={
                    onToggleVisible
                      ? (newVal) => onToggleVisible(pkg, newVal)
                      : undefined
                  }
                />
              );
            },
          },
          {
            id: "Actions",
            cell: ({
              row,
              onRowSelected,
              onRowDeleted,
            }: {
              row: { original: Package };
              onRowSelected?: (pkg: Package) => void;
              onRowDeleted?: (pkg: Package) => void;
            }) => {
              const pkg = row.original;
              const systemItem = pkg.org_id === SYSTEM_ORG_UUID;
              const packageOrgRef = orgRef ?? pkg.org_id;
              const hostedUrl = `${HOSTED_BASE_URL}/${packageOrgRef}/package/${pkg.id}`;
              return (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-8 w-8 p-0">
                      <span className="sr-only">Open menu</span>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        onRowSelected?.(row.original);
                      }}
                    >
                      <Pencil className="mr-2 h-4 w-4" />
                      {systemItem ? "View" : "Edit"}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(hostedUrl, "_blank", "noopener,noreferrer");
                      }}
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Preview
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await navigator.clipboard.writeText(hostedUrl);
                          toast.success("Package link copied");
                        } catch (err) {
                          console.error("Failed to copy package link:", err);
                          toast.error("Failed to copy package link");
                        }
                      }}
                    >
                      <Copy className="mr-2 h-4 w-4" />
                      Copy link
                    </DropdownMenuItem>
                    {!systemItem && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            onRowDeleted?.(row.original);
                          }}
                          variant="destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            },
          },
        ]
      : []),
  ];

  return columns;
};

export default createPackageColumns;
